import { DynamicHeroIcon } from '@/components/dynamic-heroicon';
import { cn } from '@/lib/utils';
import { match } from 'ts-pattern';
import { CMSText } from '../cms-text';
import type { CMSCardProps } from './index';
import { getCardThemeStyles } from './transforms';
import { AverageActivation } from '@/components/average-activation';
import { HTMLContent } from '@/components/ui/html-content';
import { CMSCTAButton } from '../cms-cta-button';
import { CMSInlineTextImage } from '../cms-inline-text-image';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import './index.css';
import { CMSCardImagePosition } from '../schemas';
import { CMSPlaceholder } from '../cms-placeholder';
import { gtmCMSCta } from '@/utils/events';

const CMSVerticalCard = ({
  cardData,
  className,
  isFromBannerWithImage = false,
  style,
}: CMSCardProps) => {
  const featureImagePosition = cardData.feature_image.image_position.key;
  const featureImageUrl = cardData.feature_image.feature_image?.url ?? '';
  const featureImageAlt = cardData.feature_image.feature_image?.alt ?? '';
  const removePadding = cardData.feature_image.remove_padding;
  const theme = cardData.theme.key;
  const cardLink = cardData.card_link;
  const cardLinkTarget = cardData.card_link_target?.key;
  const cardLinkGtmEnabled = cardData.card_link_enable_gtm;
  const cardLinkGtmData = cardData.cardLinkGtmCta;
  const withAnimation = cardData.with_animation;
  const navigate = useNavigate();
  const router = useRouterState();
  const pathname = router.location.pathname;

  const onClickCard = () => {
    if (cardLink) {
      if (cardLinkGtmEnabled) {
        gtmCMSCta(cardLinkGtmData);
      }
      // open in new tab/window
      if (cardLinkTarget === '_blank') {
        window.open(cardLink);
        return;
      }

      //curent tab
      const link = cardLink === '#' ? `${pathname}#` : cardLink;
      try {
        navigate({
          to: link,
          search: (search) => ({ ...search }),
        });
      } catch (e) {
        // TODO: Log error to sentry so we can track this issue
        console.error('link is not a valid internal link', e);
        window.location.href = cardLink;
      }
    }
  };

  const themeColour = getCardThemeStyles(theme);
  return (
    <div
      style={style}
      onClick={onClickCard}
      className={cn(
        'group flex w-full flex-col rounded-2xl lg:rounded-3xl transition-all duration-200 ease-in-out',
        {
          'flex-col-reverse':
            featureImageUrl &&
            featureImagePosition === CMSCardImagePosition.Enum.start,
          'cursor-pointer group': cardLink,

          // Add animation for before pseudo element within banner with image(first banner)
          'cardAnimation [&::before]:rounded-t-2xl lg:[&::before]:rounded-t-3xl hover:[&::before]:-translate-y-6 [&::before]:transition-all [&::before]:duration-200 [&::before]:ease-in-out':
            withAnimation,
        },
        themeColour,
        className
      )}
      // TODO: change this to CMSVerticalCard to make it explicit
      data-component="CMSCard"
    >
      {/* CMSCardContentWrapper will be used to query from parent component(css nested selector) */}
      <div
        className={cn(
          'flex flex-col p-4 md:p-6 lg:p-8 grow CMSCardContentWrapper',
          {
            'h-full': !featureImageUrl,
            'py-4! md:py-6! lg:py-8!':
              featureImageUrl && ['no-fill', 'transparent'].includes(theme),
            'group-hover:-translate-y-6 transition-all duration-200 ease-in-out':
              withAnimation,
          }
        )}
      >
        {cardData.icon_text ? (
          <DynamicHeroIcon
            iconName={cardData.icon_text}
            shape={cardData.icon_style.key}
            className="size-8 mb-3 icon"
          />
        ) : null}
        <div className="flex flex-col h-full">
          <div
            className={cn(
              'text-base lg:text-lg text-black  font-nunito700 heading',
              {
                // Set text size if it's from banner with image
                'text-[14.07px] md:text-base lg:text-xl': isFromBannerWithImage,
                'group-hover:underline': cardLink,
              }
            )}
          >
            <HTMLContent content={cardData?.heading ?? ''} />
          </div>
          <div>
            {cardData.content.map((item, index) =>
              match(item)
                .with({ type: 'text' }, (item) => (
                  <CMSText
                    key={index}
                    textData={item}
                    className={cn(
                      'text-black',

                      // Negate margin bottom for p tag coming from CMSColumn
                      '[&_p]:!mb-0',

                      // Negate text size for p tag coming from CMSColumn
                      '[&_p]:!text-sm md:[&_p]:!text-sm lg:[&_p]:!text-base',

                      {
                        // Add margin top for p tag if it's not from banner with image
                        '[&_p]:mt-2': !isFromBannerWithImage,

                        // Add margin top for p tag if it's from banner with image(different set)
                        '[&_p]:mt-[4.69px] lg:[&_p]:mt-2':
                          isFromBannerWithImage,
                      }
                    )}
                  />
                ))
                .with({ type: 'average_activation' }, () => (
                  <div key={index}>
                    <AverageActivation />
                  </div>
                ))
                .with({ type: 'cta' }, (data) => {
                  return (
                    <CMSCTAButton
                      key={index}
                      className={cn(
                        'w-full mt-4 [&_a]:block [&_a]:w-full',
                        '[&_a]:!text-sm md:[&_a]:!text-sm lg:[&_a]:!text-base',
                        {
                          'mt-2': cardData.cta.style.key === 'link',
                        }
                      )}
                      buttonClassName={'w-full'}
                      ctaData={data}
                    />
                  );
                })
                .with({ type: 'inline_text_image' }, (data) => (
                  <CMSInlineTextImage
                    key={index}
                    className={cn(
                      'mb-4 lg:mb-6 [&:not(:first-child)]:mt-4 lg:[&:not(:first-child)]:mt-6'
                    )}
                    inlineTextImageData={data}
                  />
                ))
                .with({ type: 'placeholder' }, (data) => (
                  <CMSPlaceholder
                    // Add width for app badges
                    className='[&_[data-component="CMSDownloadApp"]_img]:w-[125px] md:[&_[data-component="CMSDownloadApp"]_img]:w-auto'
                    key={index}
                    placeholderData={data}
                  />
                ))
                .otherwise(() => null)
            )}
          </div>
        </div>
        {cardData.show_cta_options && (
          <CMSCTAButton
            className={cn(
              'w-full mt-4 self-end [&_a]:block [&_a]:w-full',

              '[&_a]:!text-sm md:[&_a]:!text-sm lg:[&_a]:!text-base'
            )}
            buttonClassName={'w-full'}
            ctaData={cardData.cta}
          />
        )}
      </div>

      {featureImageUrl && (
        <div
          className={cn('w-full', {
            //remove padding bottom if image position is before the content
            'p-4 md:p-6 lg:p-8 pb-0 md:pb-0 lg:pb-0':
              featureImageUrl &&
              featureImagePosition === CMSCardImagePosition.Enum.start,

            'p-4 md:p-6 lg:p-8 pt-0 md:pt-0 lg:pt-0':
              featureImageUrl &&
              featureImagePosition === CMSCardImagePosition.Enum.end,

            'p-0 md:p-0 lg:p-0': removePadding,
          })}
        >
          <img
            className={cn('w-full', {
              'rounded-t-2xl lg:rounded-t-3xl rounded-b-none lg:rounded-b-none':
                featureImageUrl &&
                featureImagePosition === CMSCardImagePosition.Enum.start &&
                removePadding,
              'rounded-b-2xl lg:rounded-b-3xl rounded-t-none lg:rounded-t-none':
                featureImageUrl &&
                featureImagePosition === CMSCardImagePosition.Enum.end &&
                removePadding,
            })}
            src={featureImageUrl}
            alt={featureImageAlt}
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
};
export { CMSVerticalCard };
